import React, { useEffect, useState } from "react";
import PrimaryModal from "../UI/Modal/PrimaryModal";
import Loader from "../UI/Loader/Loader";
import { Form, Formik } from "formik";
import { FormikInput, FormikSelect, PrimaryButton, SecondaryButton } from "../UI";
import { ATAudienceConfigRule } from "../../schemas";
import { useUserContext } from "../../contexts/UserContext";
import {
  addAtAudienceConfigRule,
  updateAtAudienceConfigRule,
} from "../../utils/APIs";
import { permission } from "../../config/permission";
import { useContentfulContext } from "../../contexts/ContentfulContext";

const AdobeTargetAudienceConfigModel = ({
  setSuccess,
  showAdobeTargetAudienceConfigModel,
  handleCloseModal,
}) => {
  const { userData, reFetchATAudienceConfigRules, checkPermission } = useUserContext();
  const { content } = useContentfulContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const existingConfiguredRule = userData?.atAudienceConfigRule[0] || {};

  const canUserModify = existingConfiguredRule?._id
                          ? checkPermission(permission.EDIT_ADOBE_TARGET_AUDIENCES_CONFIG_RULE)
                          : checkPermission(permission.CREATE_ADOBE_TARGET_AUDIENCES_CONFIG_RULE)

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const data = {
        mbox_parameter_name: values.mbox_parameter_name,
        comparison_rule: values.comparison_rule,
        naming_convention: values.naming_convention,
      };
      let msg;

      if (existingConfiguredRule?._id) {
        await updateAtAudienceConfigRule(
          userData.token,
          userData.selectedOrganization.org_token,
          { data, at_audience_config_rule_id: existingConfiguredRule?._id }
        );
        msg = content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.AT_Config_Modal.update
      } else {
        await addAtAudienceConfigRule(
          userData.token,
          userData.selectedOrganization.org_token,
          data
        );
        msg = content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.AT_Config_Modal.success
      }

      setError("");
      setSuccess(msg);
      handleClose();
      reFetchATAudienceConfigRules();
    } catch (error) {
      setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    handleCloseModal();
  };

  const comparison_rules = [
    { name: "Equals", id: "equals" },
    {
      name: "Equals Ignore Case",
      id: "equalsIgnoreCase",
    },
    { name: "Contains", id: "contains" },
    { name: "Contains ignore case", id: "containsIgnoreCase" },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setError("");
    }, 4000);

    return () => clearTimeout(timer);
  }, [error]);

  return (
    <>
      {showAdobeTargetAudienceConfigModel && (
        <PrimaryModal
          title={content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.AT_Config_Modal.title}
          hideModal={handleClose}
          widthClass="max-w-3xl"
        >
          {loading && <Loader />}

          <div className="p-7 pt-1 space-y-6">
            <Formik
              initialValues={
                existingConfiguredRule._id
                  ? {
                      mbox_parameter_name:
                        existingConfiguredRule.mbox_parameter_name,
                      comparison_rule: existingConfiguredRule.comparison_rule,
                      naming_convention:
                        existingConfiguredRule.naming_convention,
                    }
                  : {
                      mbox_parameter_name: "",
                      comparison_rule: "",
                      naming_convention: "",
                    }
              }
              validationSchema={() => ATAudienceConfigRule(content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT)}
              onSubmit={handleSubmit}
            >
              {() => (
                <Form action="#" className="pr-3 space-y-4">
                  <p>{content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.description}</p>
                  <FormikInput
                    type="text"
                    disabled={!canUserModify}
                    id="mbox_parameter_name"
                    name="mbox_parameter_name"
                    label={content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.formLabels.mbox_parameter_name}
                    helperText={content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.help_text.mbox_parameter_name}
                  />

                  <FormikInput
                    type="text"
                    disabled={!canUserModify}
                    id="naming_convention"
                    name="naming_convention"
                    label={content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.formLabels.naming_convention}
                    helperText={content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.help_text.naming_convention}
                  />

                  <FormikSelect
                    id="comparison_rule"
                    name="comparison_rule"
                    disabled={!canUserModify}
                    label={content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.formLabels.comparison_rule}
                    helperText={content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.help_text.comparison_rule}
                    options={comparison_rules.map(r => ({ value: r.id, label: r.name }))}
                    placeholder="Select comparison rule"
                  />

                  {error && <p className="mt-2 text-sm text-red-5">{error}</p>}
                  <div className="flex flex-col md:flex-row justify-end gap-2 pt-2">
                    <div onClick={handleClose} className="w-full md:w-auto">
                      <SecondaryButton type="button" className="w-full">
                        {
                          content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT
                            .AT_Config_Modal.cancel_btn
                        }
                      </SecondaryButton>
                    </div>
                    {canUserModify && <PrimaryButton type="submit" className="w-full md:w-auto">
                      {content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.submit_cta}
                    </PrimaryButton>}
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </PrimaryModal>
      )}
    </>
  );
};

export default AdobeTargetAudienceConfigModel;
